// @flow
import React, {useCallback, useMemo, useState} from 'react';
import {Route, Switch, useHistory} from 'react-router-dom';
import PropTypes from 'prop-types';
import {usePage} from '../../page/utils';
import {useIsMobile} from '../../../utils/dom';

import {makeStyles} from '@material-ui/core/styles';
import {
  Button,
  ClickAwayListener,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {ArrowBack} from '@material-ui/icons';

import inviteFriends from '../../../assets/img/invite-referral.png';

import ReferralStep from './ReferralStep/ReferralStep';
import TablePoints from './TablePoints/TablePoints';
import RedeemPoints from './RedeemPoints/RedeemPoints';
import CustomButton from '../../../elements/CustomButton/CustomButton';

import {LEGACY_TIERS} from '../../../entities';

import wsspIcon from '../../../assets/img/wsspIcon.svg';
import fbIcon from '../../../assets/img/fbIcon.svg';
import TopReferrals from './TopReferrals/TopReferrals';
import RedeemContainer from '../containers/RedeemContainer';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import {SIZE} from '../duck';

import type {Store, User, Referred, Pagination} from '../../../entities';
import env from '../../../environment';

const {environment} = env;
const isInProd = environment === 'production';

type Props = {
  store?: Store,
  user?: User,
  referreds?: Referred[],
  fetchReferreds: () => {},
  referralError?: string,
  points?: number,
  isFetchingReferreds?: boolean,
  pagination: Pagination,
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  content: {
    paddingBottom: '5rem',
  },
  containerSubRoute: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 8px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  title: {
    marginLeft: theme.spacing(1),
    fontSize: '22px',
    fontWeight: 600,
  },
  subTitle: {
    color: '#3A3A49',
    fontWeight: 600,
    fontSize: '18px',
  },
  whiteCard: {
    minHeight: '4rem',
    padding: theme.spacing(2),
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '8px',
  },
  contentCard: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: ' center',
    justifyContent: 'center',
  },
  inviteFriend: {
    margin: '1rem 0',
  },
  containerSteps: {
    display: 'flex',
    flexDirection: 'column',
  },
  textBlack: {
    color: ' #3A3A49',
    fontWeight: '600',
    fontSize: '14px',
  },
  containerSocial: {
    display: 'flex',
    margin: theme.spacing(2, 0),
  },
  shareButton: {
    backgroundColor: '#F4F4F6',
    width: '3rem',
    height: '3rem',
    margin: theme.spacing(0, 1),
  },
  copyLink: {
    color: '#500BDB',
  },
  wrapperLink: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 1),
    width: '100%',
    background: '#F5F0FF',
    border: '1.25px solid rgba(123, 57, 255, 0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      width: '90%',
    },
  },
  containerCopyLink: {
    width: '80%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    cursor: 'default',
    fontSize: '12px',
    color: '#3A3A49',
  },
  containerButtonLink: {
    width: '60px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  linkToCopy: {
    cursor: 'default',
    fontSize: '12px',
    color: '#3A3A49',
  },
}));

export default usePage<Props>(Referral, 'Referral');

function Referral(props: Props) {
  const {
    store,
    referreds,
    points,
    fetchReferreds,
    referralError,
    isFetchingReferreds,
    pagination,
  } = props;

  const history = useHistory();
  const classes = useStyles();
  const isMobile = useIsMobile();

  // const [linkReferral] = useState('www.tiendada.com/ref/juancarlos');
  const [linkCopied, setLinkCopied] = useState(false);

  // useEffect(() => {
  //   fetchStorePoints();
  // }, [fetchStorePoints]);

  const stepsInviteFriend = [
    {
      name: 'Invita a tus amigos',
      description: 'Comparte el link de abajo',
    },
    {
      name: 'Tus amigos crean una Tienda',
      description: 'Suben un producto o generan una venta',
    },
    {
      name: '¡Tu ganas puntos!',
      description: `Canjea suscripciones de TiendaDA ${
        store && LEGACY_TIERS.includes(store.storeTier.name) ? 'PRO' : ''
      } y pronto mucho más.`,
    },
  ];

  const linkReferral = useMemo(() => {
    const domain = isInProd ? 'www.tiendada.com' : 'www.dev.tiendada.com';
    let baseText = `https://${domain}/sign-up`;
    if (store && store.referrerSlug) {
      baseText += `?referral=${store.referrerSlug}`;
    }
    return baseText;
  }, [store]);

  const shareWhatsapp = useMemo(() => {
    const newLine = encodeURIComponent('\n');
    let text = `¡Hola! En TiendaDa puedes crear una tienda virtual para tu negocio.${newLine}`;
    text += `Comienza hoy y gana 1 mes GRATIS de TiendaDa${
      store && LEGACY_TIERS.includes(store.storeTier.name) ? ' PRO' : ''
    }. _${linkReferral}_`;

    return `https://api.whatsapp.com/send?text=${text}`;
  }, [linkReferral, store]);

  const shareFacebook = useMemo(() => {
    const baseUrl = 'http://www.facebook.com/sharer.php';

    const newLine = encodeURIComponent('\n');
    let text = `¡Hola! Mira esta plataforma TiendaDa. Te permite crear una tienda virtual totalmente GRATIS y recibir tus pedidos en WhatsApp.${newLine}`;
    text += `Crea una cuenta aquí : ${linkReferral}`;

    return `${baseUrl}?p[url]=${linkReferral}&p[summary]=${text}`;
  }, [linkReferral]);

  const handleAddPage = useCallback(() => {
    if (
      !isFetchingReferreds &&
      referreds &&
      pagination &&
      pagination.total > SIZE &&
      referreds.length < pagination.total
    ) {
      fetchReferreds();
    }
  }, [fetchReferreds, isFetchingReferreds, pagination, referreds]);

  const gridTopReferres = (
    <Grid item xs={12} sm={6} md={4}>
      <div className={classes.whiteCard}>
        <div className={classes.contentCard}>
          <TopReferrals
            referreds={referreds}
            isFetchingReferreds={isFetchingReferreds}
            handleAddPage={handleAddPage}
            pagination={pagination}
          />
        </div>
      </div>
    </Grid>
  );

  const gridTablePoints = (
    <Grid item xs={12} sm={6} md={4}>
      {!isMobile && <RedeemPoints points={points} error={referralError} />}
      <div className={classes.whiteCard}>
        <div className={classes.contentCard}>
          <TablePoints />
        </div>
      </div>
    </Grid>
  );

  const otherGrids = useMemo(
    () => (
      <>
        {isMobile ? (
          <>
            {gridTablePoints}
            {gridTopReferres}
          </>
        ) : (
          <>
            {gridTopReferres}
            {gridTablePoints}
          </>
        )}
      </>
    ),
    [gridTablePoints, gridTopReferres, isMobile]
  );

  const copyButton = (
    <ClickAwayListener onClickAway={() => setLinkCopied(false)}>
      <Tooltip
        placement="right"
        arrow
        PopperProps={{
          disablePortal: true,
        }}
        onClose={() => setLinkCopied(false)}
        open={linkCopied}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        leaveDelay={1000}
        title="¡Link copiado!"
      >
        <Button
          className={classes.copyLink}
          disabled={!store || (store && !store.referrerSlug)}
          onClick={() => {
            const textCopied = store ? linkReferral : '';
            store &&
              navigator &&
              navigator.clipboard &&
              navigator.clipboard
                .writeText(textCopied)
                .then(() => {
                  setLinkCopied(true);
                })
                .catch(e => console.error(e));
          }}
        >
          Copy
        </Button>
      </Tooltip>
    </ClickAwayListener>
  );

  return (
    <div className={classes.root}>
      {referralError && <SnackBar mode="error" message={referralError} />}

      <div className={classes.containerSubRoute}>
        <IconButton
          variant="contained"
          size="small"
          // component={Link}
          className={classes.backButton}
          // to={subRoute ? '/profile/referral' : '/profile'}
          onClick={() => history.goBack()}
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.title}>Refiere a tus amigos</Typography>
      </div>
      <Switch>
        <Route exact path="/profile/referral">
          <div className={classes.content}>
            {isMobile && <RedeemPoints points={points} error={referralError} />}
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={4}>
                <div className={classes.whiteCard}>
                  <div className={classes.contentCard}>
                    <Typography className={classes.subTitle}>
                      ¡Invita a tus amigos!
                    </Typography>
                    <img
                      src={inviteFriends}
                      alt="Invita a tus amigos"
                      className={classes.inviteFriend}
                    />
                    <div className={classes.containerSteps}>
                      {stepsInviteFriend.map((step, i) => (
                        <ReferralStep
                          key={`step-referral-${i + 1}`}
                          step={step}
                          number={i + 1}
                        />
                      ))}
                    </div>
                    <div className={classes.wrapperLink}>
                      <div className={classes.containerCopyLink}>
                        {linkReferral}
                      </div>
                      <div className={classes.containerButtonLink}>
                        {copyButton}
                      </div>
                    </div>
                    <Typography className={classes.textBlack}>
                      O comparte
                    </Typography>
                    <div className={classes.containerSocial}>
                      <CustomButton
                        className={classes.shareButton}
                        href={shareFacebook}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img src={fbIcon} alt="share fb" />
                      </CustomButton>
                      <CustomButton
                        className={classes.shareButton}
                        href={shareWhatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img src={wsspIcon} alt="share wssp" />
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </Grid>
              {otherGrids}
            </Grid>
          </div>
        </Route>
        <Route path="/profile/referral/redeem" component={RedeemContainer} />
      </Switch>
    </div>
  );
}

Referral.propTypes = {
  store: PropTypes.object,
  user: PropTypes.object,
  referreds: PropTypes.array,
  points: PropTypes.number,
  fetchReferreds: PropTypes.func.isRequired,
  referralError: PropTypes.string,
  isFetchingReferreds: PropTypes.bool,
  pagination: PropTypes.object,
};
